<template>
  <div class="tour-program">
    <slot name="title" />
    <div class="tour-program__toolbar u-text-green-dark">
      <button-icon
        v-if="tour.id && offerId"
        reverse
        class="tour-program__toolbar-button"
        @click="showPopupEmail"
      >
        <template #icon>
          <icon icon="envelope" />
        </template>
        <span v-html="$t('popupClaimPdf.trigger')"></span>
      </button-icon>
      <dropdown-trigger
        :extended="allDaysExtended"
        :label-extended="$t('dropdownTrigger.days.extended')"
        :label-shrunk="$t('dropdownTrigger.days.shrunk')"
        @click="toggleAllDays"
      />
    </div>
    <div class="tour-program__days">
      <extension
        v-for="(day, index) in days"
        :key="day.id"
        v-model="day.extended"
        class="tour-program__day"
      >
        <template #header>
          {{ $t("serpDetail.program.day") }} {{ index + 1 }}.
          <span v-html="day.title"></span>
        </template>
        <template #body>
          <div class="tour-program__day-content">
            <extension-cropped>
              <div v-html="day.description"></div>
            </extension-cropped>
            <gallery
              v-if="day.photo && day.photo.length"
              class="tour-program__day-gallery"
              :gallery="day.photo"
            />
          </div>
        </template>
      </extension>
    </div>
    <no-ssr>
      <div v-if="map.display" class="tour-program__map">
        <img v-if="map.display_src" v-lazy="map.src" alt="" class="u-img-full" />
        <map-days v-else-if="mapRoute" :coordinates="map.coordinates" />
      </div>
    </no-ssr>

    <div class="tour-program__block">
      <div class="tour-program__block-title"
           v-text="tour.activity_data.title"/>

      <div class="tour-program__block-value">
        <span>{{ tour.activity_data.title_select }}</span>
        <circles-scale :position="tour.activity/2"/>
      </div>

      <div class="tour-program__block-description"
           v-text="tour.activity_data.description"/>

      <div v-if="tour.activity_text" class="tour-program__comment g-tile">
        <div
          class="tour-program__comment-title font-semibold"
          v-html="$t('serpDetail.program.comment')"
        ></div>
        <extension-cropped>
          <div
            class="tour-program__comment-container"
            v-html="tour.activity_text"
          ></div>
        </extension-cropped>
      </div>
    </div>

    <no-ssr>
    <popup-claim-pdf
      v-model="popups.email"
      :tour-id="tour.id"
      :offer-id="offerId"
    />
    </no-ssr>
  </div>
</template>

<script>
import ButtonIcon from "@/components/ui-kit/button-icon/index.vue";
import Icon from "@/components/icons/index.vue";
import DropdownTrigger from "@/components/ui-kit/dropdown-trigger/index.vue";
import CirclesScale from "@/components/ui-kit/circles-scale/index.vue";
import Gallery from "@/components/organisms/gallery/index.vue";
import ExtensionCropped from "@/components/ui-kit/extension-cropped/index.vue";
import Extension from "@/components/ui-kit/extension/index.vue";
import PopupClaimPdf from "@/components/organisms/popup-claim-pdf/index.vue";
import noSSR from "vue-no-ssr";
import { ytEvents } from "@/plugins/analytics";

export default {
  components: {
    ButtonIcon,
    Icon,
    DropdownTrigger,
    Gallery,
    ExtensionCropped,
    Extension,
    MapDays: vueOptions.ssr ? {} : () => import("@/components/ui-kit/map-days/index.vue"),
    PopupClaimPdf,
    CirclesScale,
    "no-ssr": noSSR,
  },
  props: {
    tour: {
      type: Object,
      required: true,
    },
    offerId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      days: [],
      popups: {
        email: false,
      },
    };
  },
  computed: {
    allDaysExtended() {
      return this.days.every(({ extended }) => extended);
    },
    map() {
      return this.tour.map || {};
    },
    mapRoute() {
      return this.map.coordinates?.route;
    },
  },
  watch: {
    "tour.days": {
      immediate: true,
      handler(days) {
        this.days = days.map((day, index) => ({
          ...day,
          extended: index === 0 || vueOptions.ssr,
        }));
      },
    },
  },
  methods: {
    toggleAllDays() {
      this.days = this.days.map((day) => ({
        ...day,
        extended: !this.allDaysExtended,
      }));
    },
    showPopupEmail() {
      this.popups.email = true;
      ytEvents?.track('Tour detail Day by day', 'Click Get pdf button', location.pathname);
    }
  },
};
</script>

<style lang="scss" scoped>
.tour-program {
  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    &-button {
      &:hover {
        >>> * {
          color: var.$green-hover;
        }
      }

      svg {
        transition: var.$transition-default;
      }
    }
  }

  &__day {
    &-content {
      @include mixin.size-md-only {
        display: flex;
        flex-direction: column-reverse;
      }

      @include mixin.size-xl-min {
        display: flex;
        flex-direction: column-reverse;
      }
    }

    &-gallery {
      overflow: hidden;
      margin-top: 16px;

      @include mixin.size-md-only {
        margin-top: 0;
        margin-bottom: 24px;
      }

      @include mixin.size-xl-min {
        margin-top: 0;
        margin-bottom: 24px;
      }
    }
  }

  &__map,
  &__comment {
    @include mixin.size-md-only {
      margin-top: 40px;
    }

    @include mixin.size-xl-min {
      margin-top: 40px;
    }
  }

  &__comment {
    padding: 24px;
    margin-top: 22px;

    @include mixin.size-md-only {
      margin-top: 28px;
    }

    @include mixin.size-xl-min {
      margin-top: 28px;
    }

    &-title {
      margin-bottom: 20px;
    }

    &-container {
      white-space: pre-line;
    }
  }
}
</style>
